/**
 * Repair information: vehicle-scoped search over the published vehicle
 * knowledge base (`GET /repair-information/vehicles`,
 * `POST /repair-information/search`). The user always picks a vehicle first.
 */

export type RepairLanguage = "de" | "en";

export type RepairModule =
  | "labour_times"
  | "technical_data"
  | "manuals"
  | "bulletins"
  | "maintenance";

export const REPAIR_MODULES: RepairModule[] = [
  "labour_times",
  "technical_data",
  "manuals",
  "bulletins",
  "maintenance",
];

export interface VehicleRef {
  typeId: number;
  bodyId: number;
  countryCode: string;
}

export interface Vehicle extends VehicleRef {
  ktype?: number | null;
  make?: string | null;
  model?: string | null;
  typeName?: string | null;
  languages: RepairLanguage[];
}

export interface VehiclesResponse {
  dataset: { id: string; contentHash?: string | null; updatedAt?: string | null };
  vehicles: Vehicle[];
}

export interface RepairSearchRequest {
  query: string;
  vehicle: VehicleRef;
  /** Display language of titles and facts. */
  language: RepairLanguage;
  /** Language the query is written in (defaults to `language`). */
  queryLanguage?: RepairLanguage;
  /** Restrict to one module; all modules otherwise. */
  module?: RepairModule | null;
  /** Minimum fused score (0–100) a hit needs; no result-count cap. */
  threshold?: number;
}

/** `ok`, `no_match` (every module below its gate), `no_coverage` (vehicle or
 *  language not published), `degraded` (a stage fell back; see `degraded`). */
export type RepairStatus = "ok" | "no_match" | "no_coverage" | "degraded";

/** One fact of a posting; its keys depend on the module. */
export type RepairFact = Record<string, string | number | boolean | null>;

export interface RepairHit {
  /** Stable source path, e.g. `labour_times:1:13:435:7` (the citation key). */
  key: string;
  postingKey: string;
  module: RepairModule;
  title: string;
  breadcrumb: string[];
  facts: RepairFact[];
  source: Record<string, unknown>;
  /** Fused 0–100 ranking score. */
  score: number;
  semanticScore: number;
  lexicalScore: number;
  titleScore: number;
  routeSignal: number;
}

export interface ModuleResult {
  module: RepairModule;
  status: string;
  hits: number;
  candidates: number;
}

export interface RepairSearchResponse {
  status: RepairStatus;
  vehicle: Record<string, unknown>;
  language: RepairLanguage;
  selectedModule?: RepairModule | null;
  answer?: string | null;
  citations: string[];
  hits: RepairHit[];
  moduleResults: ModuleResult[];
  degraded: string[];
}
